import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme, API_URL } from "../App";
import Sidebar from "../components/Sidebar";
import RequestPanel from "../components/RequestPanel";
import EnvPanel from "../components/EnvPanel";
import ThemePicker from "../components/ThemePicker";

export default function Dashboard() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { C } = useTheme();

  const [workflows, setWorkflows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [env, setEnv] = useState({});
  const [selected, setSelected] = useState(null);
  
  const [panel, setPanel] = useState(null);
  const [toast, setToast] = useState(null);
  const [use24, setUse24] = useState(true);

  // Live Clock
  const [now, setNow] = useState(new Date());
  useEffect(()=>{ const t=setInterval(()=>setNow(new Date()),1000); return()=>clearInterval(t); },[]);

  const togglePanel = p => setPanel(x => x === p ? null : p);
  const notify = (msg, err=false) => { setToast({msg,err}); setTimeout(()=>setToast(null), 2500); };

  const api = axios.create({ baseURL: API_URL });

  const fetchWorkflows = useCallback(() => { api.get("/workflows").then(r => setWorkflows(r.data)).catch(e => { if(e.response?.status===401) logout(); }); }, [api, logout]);
  const fetchLogs = useCallback(() => { api.get("/logs").then(r => setLogs(r.data)).catch(()=>{}); }, [api]);
  const fetchEnv = useCallback(() => { api.get("/env").then(r => setEnv(r.data)).catch(()=>{}); }, [api]);

  useEffect(() => {
    if (!user) return;
    fetchWorkflows();
    fetchLogs();
    fetchEnv();
    const t = setInterval(fetchLogs, 6000);
    return () => clearInterval(t);
  }, [fetchWorkflows, fetchLogs, fetchEnv, user]);

  const saveEnv = async (newEnv) => {
    try {
      await api.put("/env", newEnv);
      setEnv(newEnv);
      notify("Environment variables saved");
    } catch { notify("Failed to save environment variables", true); }
  };

  const handleSaveWf = async (wf) => {
    try {
      let updated;
      if (selected?.id) {
        const { data } = await api.put(`/workflows/${selected.id}`, wf);
        updated = workflows.map(w => w.id === selected.id ? data : w);
        notify(`"${wf.name}" updated`);
      } else {
        const { data } = await api.post("/workflows", wf);
        updated = [data, ...workflows];
        notify(`"${wf.name}" created`);
      }
      setWorkflows(updated);
      setSelected(updated.find(w => w.name === wf.name)); 
    } catch(err) {
      throw new Error(err.response?.data?.error || "Save failed");
    }
  };

  const handleToggleWf = async (id) => {
    try {
      const { data } = await api.post(`/workflows/${id}/toggle`);
      setWorkflows(wfs => wfs.map(w => w.id === id ? { ...w, active: data.active } : w));
      if (selected?.id === id) {
        setSelected(prev => ({ ...prev, active: data.active }));
      }
    } catch(err) { notify(err.response?.data?.error || "Toggle failed", true); }
  };

  const handleDeleteWf = async (id) => {
    try {
      await api.delete(`/workflows/${id}`);
      setWorkflows(wfs => wfs.filter(w => w.id !== id));
      if (selected?.id === id) setSelected(null);
      notify("Deleted");
    } catch(err) { notify("Delete failed", true); }
  };

  const handleDeleteLog = async (id) => {
    try {
      await api.delete(`/logs/${id}`);
      setLogs(ls => ls.filter(l => l.id !== id));
    } catch {}
  };

  const handleClearLogs = async () => {
    if (!window.confirm("Clear all logs?")) return;
    try {
      await api.delete("/logs");
      setLogs([]);
      notify("Logs cleared");
    } catch {}
  };

  const activeCount = workflows.filter(w=>w.active).length;
  const errorCount  = logs.filter(l=>l.status==="error").length;

  return (
    <div className="app-container">
      {/* Top bar */}
      <div className="top-bar">
        <span style={{ fontSize:16 }}>⚡</span>
        <span style={{ fontWeight:800, fontSize:15, letterSpacing:-0.5 }}>AutoFlow</span>
        <div style={{ width:1, height:20, background:C.border, margin:"0 10px" }}/>
        <div style={{ display:"flex", gap:14, fontSize:12 }}>
          <span><b style={{ color:"#3fb950" }}>{activeCount}</b> <span style={{ color:C.muted }}>active</span></span>
          <span><b style={{ color:"#f85149" }}>{errorCount}</b> <span style={{ color:C.muted }}>errors</span></span>
          <span><b style={{ color:C.muted }}>{logs.length}</b> <span style={{ color:"var(--color-faint)" }}>logs</span></span>
        </div>
        <div style={{ flex:1 }}/>
        
        <span style={{ fontSize:12, color: C.muted, fontWeight:600 }}>{user ? user.email : "Guest Mode"}</span>
        
        {[{id:"env",label:'{ } Env'},{id:"theme",label:"🎨 Theme"}].map(({id,label})=>(
          <button key={id} type="button" onClick={()=>togglePanel(id)} style={{
            background:panel===id?C.panel:"transparent",
            border:`1px solid ${panel===id?C.muted:C.border}`,
            borderRadius:6, padding:"4px 10px",
            color:panel===id?C.text:C.muted,
            cursor:"pointer", fontSize:11, fontWeight:600 }}>{label}</button>
        ))}
        <button type="button" onClick={()=>setUse24(x=>!x)} style={{ background:C.panel,
          border:`1px solid ${C.border}`, borderRadius:6, padding:"4px 10px",
          cursor:"pointer", color:C.text, fontSize:11, fontWeight:700 }}>
          {use24?"24h":"12h"}
        </button>
        <span style={{ fontFamily:"monospace", fontSize:12, color:C.text }}>
            {now.toLocaleTimeString([],{ hour12:!use24, hour:"2-digit", minute:"2-digit", second:"2-digit" })}
        </span>
        {user ? (
          <button onClick={logout} className="btn btn-ghost" style={{padding: "4px 10px", fontSize: 11}}>Logout</button>
        ) : (
          <button onClick={() => navigate("/login")} className="btn btn-primary" style={{padding: "4px 10px", fontSize: 11}}>Login / Register</button>
        )}
      </div>

      {toast && (
        <div className="toast" style={{
          background:toast.err?"#200d0d":"#0d200d",
          border:`1px solid ${toast.err?"#f8514940":"#3fb95040"}`,
          color:toast.err?"#f85149":"#3fb950" }}>{toast.msg}</div>
      )}

      {panel==="env"   && <EnvPanel env={env} onChange={saveEnv} onClose={()=>setPanel(null)}/>}
      {panel==="theme" && <ThemePicker onClose={()=>setPanel(null)}/>}

      <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
        <Sidebar
          workflows={workflows} logs={logs} selected={selected}
          onSelect={setSelected} onDeleteLog={handleDeleteLog}
          onClearLogs={handleClearLogs} onRefresh={()=>{fetchWorkflows();fetchLogs();}}/>
        <RequestPanel
          key={selected?.id??"__new__"}
          initial={selected} env={env}
          onSaved={handleSaveWf} onToggle={handleToggleWf}
          onDelete={handleDeleteWf} onRefresh={fetchLogs}/>
      </div>
    </div>
  );
}
